/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import moment, { MomentInput } from 'moment'

/**
 * the class for common utilities
 *
 * @class CommonUtils
 */
export class CommonUtils {
	static defaultDateFormat = 'YYYY-MM-DDThh:mm:ss'
	static defaultDate = new Date('0001-01-01T00:00:00')
	static momentDefaultDate = moment('0001-01-01T00:00:00')

	static isMinDate(inputDate?: MomentInput): boolean {
		if (!inputDate) {
			return false
		}

		if (typeof inputDate === 'string') {
			if (inputDate.trim() === '') {
				return false
			}
			return moment(inputDate).format(this.defaultDateFormat) === this.momentDefaultDate.format(this.defaultDateFormat)
		}
		return inputDate instanceof Date && inputDate.getTime() === this.defaultDate.getTime()
	}

	static isUndefinedOrNull<T>(input: T | undefined | null): boolean {
		return typeof input === 'undefined' || input === null
	}

	static isNullUndefinedEmpty(input: string | undefined | null): boolean {
		if (!input) {
			return true
		}

		return typeof input === 'string' && input.trim() === ''
	}

	static DateRange(startDate: string | Date, endDate: string | Date, calYear = false): string {
		const startDateIsValid = this.isValidDate(startDate)
		const endDateIsValid = this.isValidDate(endDate)

		if (calYear && startDateIsValid && endDateIsValid) {
			const momentStartDate = moment(startDate)
			const momentEndDate = moment(endDate)

			const smonth = momentStartDate.format('M')
			const sDay = momentStartDate.format('D')
			const sYear = momentStartDate.format('YYYY')

			const emonth = momentEndDate.format('M')
			const eDay = momentEndDate.format('D')
			const eYear = momentEndDate.format('YYYY')

			if (smonth === '1' && sDay === '1' && emonth === '12' && eDay === '31' && sYear === eYear) return 'Calendar Year ' + sYear
		}

		const getDate = (input: any): string => new Date(input).toLocaleDateString()

		let result = ''

		if (startDateIsValid) {
			result = getDate(startDate)
		}

		if (endDateIsValid) {
			if (result !== '') {
				result += '-'
			}
			result += getDate(endDate)
		}
		return result
	}

	static getDate(date: MomentInput, format: string): string {
		if (!this.isValidDate(date)) {
			return ''
		}

		return moment(date).format(format)
	}

	static isValidDate(date: MomentInput): boolean {
		if (this.isMinDate(date)) {
			return false
		}

		return moment(date).isValid()
	}

	static groupBy<K, V>(list: V[], keyGetter: (TValue) => K): Map<K, V> {
		const map = new Map()
		list.forEach(item => {
			const key = keyGetter(item)
			const collection = map.get(key)
			if (!collection) {
				map.set(key, [item])
			} else {
				collection.push(item)
			}
		})
		return map
	}
	static toDisplayDate(date: Date | string | null | undefined): string {
		const dateFormat = 'MM/DD/YYYY'
		return this.isValidDate(date) ? moment(date).format(dateFormat) : ''
	}

	static toDisplayPhoneNumber(phoneNumber: string | null | undefined): string {
		let modifiedValue = phoneNumber ? phoneNumber : ''
		if (phoneNumber) {
			modifiedValue = phoneNumber.substring(0, 3) + '-' + phoneNumber.substring(3, 6) + '-' + phoneNumber.substring(6)
		}
		return modifiedValue
	}

	static compareProperty<T>(first: T, second: T): number {
		if (!first && !second) {
			return 0
		}

		if (!first) return 1
		if (!second) return -1

		if (typeof first === 'number' && typeof second === 'number') {
			return first - second
		}

		if (this.isDateType(first) && this.isDateType(second)) {
			return (first as Date).valueOf() - (second as Date).valueOf()
		}

		if (typeof first === 'string' && typeof second === 'string') {
			return first.localeCompare(second)
		}

		return 0
	}

	//Note: the explicit parameter type check in eslint is disabled for this function because it is used as a typeguard to check a type.
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public static isDateType(input: any): input is Date {
		return Object.prototype.toString.call(input) === '[object Date]'
	}

	public static datesAreEqual(sourceDate: Date | string | undefined | null, destDate: Date | string | null | undefined): boolean {
		// Both does't have data
		if (!sourceDate && !destDate) {
			return true
		}

		// Any one is having data
		if ((!sourceDate && destDate) || (sourceDate && !destDate)) {
			return false
		}

		// type is string and has valid data
		if (typeof sourceDate === 'string' && typeof destDate === 'string') {
			if (moment(sourceDate).isValid() && moment(destDate).isValid()) {
				return new Date(sourceDate).getTime() === new Date(destDate).getTime()
			}
		} else if (sourceDate instanceof Date && destDate instanceof Date) {
			return sourceDate.getTime() === destDate.getTime()
		}

		return false
	}

	public static isDifferent(srcContent: string | number | Date | null | undefined, destContent: string | number | Date | null | undefined): boolean {
		// Both does't have data
		if (!srcContent && !destContent) {
			return false
		}
		// Any one is having data
		if ((!srcContent && destContent) || (!destContent && srcContent)) {
			return true
		}

		if (typeof srcContent === 'string' && typeof destContent === 'string') {
			return srcContent.trim().toLocaleLowerCase() !== destContent.trim().toLocaleLowerCase()
		} else if (typeof srcContent === 'number' && typeof destContent === 'number') {
			return srcContent !== destContent
		}
		return false
	}

	public static isDVAddressDifferent(address1, address2, destContent): boolean {
		if (!destContent) {
			return false
		}
		// Add Address1 & Address2 and compare it with DV address field
		const combinedAddress = (CommonUtils.isNullUndefinedEmpty(address1) ? '' : address1.trim()) + (CommonUtils.isNullUndefinedEmpty(address2) ? '' : ' ' + address2.trim())
		return CommonUtils.isDifferent(combinedAddress, destContent)
	}

	public static isDVDataDifferent(srcContent: string | number | Date | null | undefined, destContent: string | number | Date | null | undefined): boolean {
		// Both does't have data
		if (!srcContent && !destContent) {
			return false
		}
		//if DV doest have datta
		if (!destContent) {
			return false
		}

		//if source is not having data & destination having
		if (!srcContent && destContent) {
			return true
		}

		if (typeof srcContent === 'string' && typeof destContent === 'string') {
			return srcContent.trim().toLocaleLowerCase() !== destContent.trim().toLocaleLowerCase()
		} else if (typeof srcContent === 'number' && typeof destContent === 'number') {
			return srcContent !== destContent
		} else if (srcContent instanceof Date && destContent instanceof Date) {
			return srcContent.getTime() === destContent.getTime()
		}
		return false
	}
	public static isZipCodeDifferent(srcContent, destContent): boolean {
		// Both does't have data
		if (!srcContent && !destContent) {
			return false
		}
		//if DV doest have datta
		if (!destContent) {
			return false
		}

		//if source is not having data & destination having
		if (!srcContent && destContent) {
			return true
		}

		//if zipcode is 9 digit length

		if (typeof srcContent === 'string' && typeof destContent === 'string') {
			if (srcContent.includes('-') || destContent.includes('-')) return srcContent.replace('-', '').trim() !== destContent.replace('-', '').trim()
			else return srcContent.trim() !== destContent.trim()
		}
		return false
	}
}
